/**
 * useDraftDiscardActions
 *
 * Shared implementation of "Discard draft" and "Discard session" used by
 * both `DraftModeContext` and `WorkflowOverflowMenu`. The menu lives in
 * `WorkflowViewTabs` which renders ABOVE `DraftModeProvider`, so it can't
 * consume the context directly — this hook gives both call sites the same
 * full sequence (canvas refresh + dirty-state reset + backend persist)
 * instead of letting the menu fall back to the bare store action, which
 * only mutated in-memory draft state and left the canvas + backend stale.
 *
 * Semantics:
 * - `discardDraft` — sync the draft with the published version. Updates
 *   in-memory draft, reloads the canvas from prod artifacts, clears dirty
 *   flags, and persists via `discardDraftApi` so the next reload doesn't
 *   resurrect the discarded draft.
 * - `discardSession` — revert in-memory state to the snapshot taken when
 *   the session started, then persist via `saveDraftNow`. Persisting on
 *   discard mirrors the user's expectation that "with manual save we
 *   should overwrite" — without the explicit save, a manual-save user who
 *   discards the session and reloads would get the un-discarded draft
 *   back.
 */

import { useCallback } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { useSessionHistoryStore } from '@/stores/sessionHistoryStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useDraftSave } from '@/hooks/useDraftSave';
import { discardDraft as discardDraftApi } from '@/services/draftWorkflowApi';
import { telemetryService } from '@/services/telemetryService';

export interface UseDraftDiscardActionsResult {
  /** Discard the draft, resetting it to the published version (persists to backend). */
  discardDraft: () => Promise<void>;
  /** Revert in-memory state to the session-start snapshot (persists to backend). */
  discardSession: () => Promise<void>;
}

export interface UseDraftDiscardActionsOptions {
  /**
   * Workflow URL name. When omitted, the backend persist steps are skipped
   * and the discard remains local-only (useful for tests / read-only views
   * where there is no concrete workflow to persist against).
   */
  workflowName?: string;
}

export function useDraftDiscardActions({
  workflowName,
}: UseDraftDiscardActionsOptions = {}): UseDraftDiscardActionsResult {
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();

  const discardDraftAction = useDraftModeStore((s) => s.discardDraft);
  const setWorkflowFiles = useWorkflowStore((s) => s.setWorkflowFiles);
  const markWorkflowClean = useWorkflowStore((s) => s.markClean);
  const markConnectionsClean = useConnectionStore((s) => s.markClean);
  // Re-init the connection store from the chosen baseline on discard.
  // `markClean` only flips the dirty flag — it doesn't touch the actual
  // references/mapping/originalConnectionsConfig. Without an explicit
  // re-init the store keeps the draft's connection edits in memory, so a
  // subsequent save would serialize discarded refs via
  // `exportToConnectionsConfig` (which reads from those same fields)
  // even though the workflow store has already been reset.
  const initializeConnectionsFromBaseline = useConnectionStore(
    (s) => s.initializeFromWorkflow,
  );

  // `useDraftSave` is instantiated per-consumer (it owns local React Query
  // mutation state). Multiple instances are safe — they all serialize the
  // same shared stores and hit the same backend endpoint.
  const { save: saveDraft } = useDraftSave({ workflowName: workflowName ?? '' });

  const discardDraft = useCallback(async () => {
    const prodWorkflow = useDraftModeStore.getState().prodWorkflow;
    const prodConnections = useDraftModeStore.getState().prodConnections;

    // Update in-memory draft artifacts first so the UI reflects the discard
    // immediately, even if the API call later fails.
    discardDraftAction();
    telemetryService.trackEvent('workflow.draft.discarded');

    if (prodWorkflow) {
      // Reload the visible canvas from production artifacts. Without this
      // the workflow store keeps the stale (discarded) draft content.
      setWorkflowFiles(prodWorkflow, prodConnections);
      // `setWorkflowFiles` only touches the workflow store — re-extract
      // connection references/mapping from the prod baseline so the
      // connection store's serialization path can't resurrect discarded
      // refs on the next save.
      initializeConnectionsFromBaseline(prodWorkflow, prodConnections ?? null);
    }

    // Both stores have their own dirty flag — neither is touched by the
    // store-level discardDraft, so reset them explicitly.
    markWorkflowClean();
    markConnectionsClean();

    // Persist the discard to the backend so the next reload doesn't bring
    // the discarded draft back. Skipped when we don't have enough context
    // (e.g. no workflow name, no project/app selected).
    if (workflowName && selectedProject && selectedApp && prodWorkflow) {
      try {
        await discardDraftApi({
          projectName: selectedProject,
          appName: selectedApp.name,
          getToken: getOttoToken,
          workflowName,
          prodWorkflow: {
            definition: prodWorkflow.definition,
            kind: prodWorkflow.kind || 'Stateful',
          },
          prodConnections: prodConnections ?? undefined,
        });
      } catch (err) {
        // Don't fail the UI operation — local state is already updated.
        // The user sees the discard, and the backend will catch up on the
        // next autosave / manual save.
        console.warn('[useDraftDiscardActions] Failed to persist discardDraft to API:', err);
      }
    }
  }, [
    discardDraftAction,
    setWorkflowFiles,
    initializeConnectionsFromBaseline,
    markWorkflowClean,
    markConnectionsClean,
    workflowName,
    selectedProject,
    selectedApp,
    getOttoToken,
  ]);

  const discardSession = useCallback(async () => {
    // Capture the session-start snapshot BEFORE the revert so we can
    // re-init the connection store from the same baseline that
    // `revertToSessionStart` applies to the workflow store. Without this
    // the connection store keeps the in-session edits (refs/mapping)
    // even though `markClean` clears the dirty flag, and a subsequent
    // save would serialize them via `exportToConnectionsConfig`.
    const sessionStart = useSessionHistoryStore.getState().history[0];
    // Revert in-memory artifacts to the session-start snapshot. This calls
    // setWorkflowFiles internally, so the canvas refreshes.
    useSessionHistoryStore.getState().revertToSessionStart();
    if (sessionStart) {
      initializeConnectionsFromBaseline(
        sessionStart.workflow,
        sessionStart.connections,
      );
    }
    // setWorkflowFiles clears the workflow store's dirty flag, but the
    // connection store has its own — clear it explicitly so the Unsaved
    // badge / save buttons aren't stuck enabled after the revert.
    markWorkflowClean();
    markConnectionsClean();
    telemetryService.trackEvent('workflow.draft.session_discarded');

    // Persist the revert. In autosave mode the autosave loop would catch
    // up shortly, but in manual-save mode the user would otherwise have to
    // remember to click Save afterwards — and a reload before that save
    // would resurrect the discarded session edits.
    if (workflowName && selectedProject && selectedApp) {
      try {
        await saveDraft();
      } catch (err) {
        console.warn('[useDraftDiscardActions] Failed to persist discardSession to API:', err);
      }
    }
  }, [
    markWorkflowClean,
    markConnectionsClean,
    initializeConnectionsFromBaseline,
    workflowName,
    selectedProject,
    selectedApp,
    saveDraft,
  ]);

  return { discardDraft, discardSession };
}
